import * as yup from 'yup';
import { FMS_TESTS, MOBILITY_TESTS, POSTURE_FIELDS } from './assessmentFormConfig';
import {
  HEIGHT_CM_MAX,
  HEIGHT_CM_MIN,
  validateHeightCm,
  validateWaistCm,
  validateWeightKg,
  WAIST_CM_MAX,
  WAIST_CM_MIN,
  WEIGHT_KG_MAX,
  WEIGHT_KG_MIN,
} from './healthCalculations';

const optionalNumber = () =>
  yup
    .number()
    .transform((value, original) => (original === '' || original == null ? undefined : value))
    .nullable();

const rangedNumber = (min, max, label) =>
  optionalNumber()
    .min(min, `${label} must be at least ${min}`)
    .max(max, `${label} must be at most ${max}`);

const scoreField = () =>
  yup
    .number()
    .transform((value, original) => (original === '' || original == null ? undefined : value))
    .min(0, 'Min 0')
    .max(3, 'Max 3')
    .required('Required');

const optionalString = () =>
  yup
    .string()
    .nullable()
    .transform((value, original) => (
      original === null || original === undefined ? '' : String(original)
    ));

const humanizeValidationMessage = (path, message) => {
  if (!message || typeof message !== 'string') return message;

  const fieldKey = path?.split('.').pop() || '';
  const label = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (/cannot be null/i.test(message)) {
    return `${label} is required`;
  }
  if (/must be a `?string`?/i.test(message)) {
    return `${label} must be text`;
  }
  if (/must be a `?number`?/i.test(message)) {
    return `${label} must be a number`;
  }
  if (message.includes('.') && message.includes(fieldKey)) {
    return message.replace(/^[\w.]+\./, '').replace(/_/g, ' ');
  }
  return message;
};

const postureShape = Object.fromEntries(
  POSTURE_FIELDS.map((f) => [f.key, yup.string().required(`${f.label} is required`)])
);

const setNestedValue = (obj, path, value) => {
  const parts = path.split('.');
  let current = obj;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      current[part] = value;
      return;
    }
    if (!current[part] || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part];
  });
};

export const validateAssessmentStep = async (step, values) => {
  try {
    await stepSchemas[step].validate(values, { abortEarly: false });
    return { errorMap: {}, items: [] };
  } catch (err) {
    const errorMap = {};
    const items = err.inner?.length ? err.inner : err.path ? [err] : [];
    items.forEach((e) => {
      if (!e.path) return;
      setNestedValue(errorMap, e.path, humanizeValidationMessage(e.path, e.message));
    });
    return { errorMap, items };
  }
};

export const stepSchemas = [
  yup.object({}),
  yup.object({
    health_screening: yup.object({
      height_cm: rangedNumber(HEIGHT_CM_MIN, HEIGHT_CM_MAX, 'Height')
        .required('Height is required')
        .test('height-valid', 'Invalid height', (v) => !validateHeightCm(v)),
      weight_kg: rangedNumber(WEIGHT_KG_MIN, WEIGHT_KG_MAX, 'Weight')
        .required('Weight is required')
        .test('weight-valid', 'Invalid weight', (v) => !validateWeightKg(v)),
      blood_pressure: optionalString(),
      resting_heart_rate: optionalNumber().min(30).max(220),
      waist_cm: rangedNumber(WAIST_CM_MIN, WAIST_CM_MAX, 'Waist')
        .test('waist-valid', 'Invalid waist', (v) => v == null || v === '' || !validateWaistCm(v)),
      hip_cm: optionalNumber(),
      body_fat_pct: optionalNumber(),
      medical_conditions: optionalString(),
      medications: optionalString(),
      exercise_experience: optionalString(),
      emergency_contact: optionalString(),
      notes: optionalString(),
      risk_override_comment: optionalString(),
    }),
  }),
  yup.object({
    posture: yup.object({
      ...postureShape,
      notes: yup.string(),
    }),
  }),
  yup.object({
    functional_movement: yup.object(
      Object.fromEntries(FMS_TESTS.map((t) => [t.key, scoreField()]))
    ),
  }),
  yup.object({
    mobility: yup.object(
      Object.fromEntries(MOBILITY_TESTS.map((t) => [t.key, scoreField()]))
    ),
  }),
  yup.object({
    fitness_testing: yup.object({
      cardio_method: yup.string(),
      cardio_result: optionalNumber().min(0),
      push_ups: optionalNumber().min(0),
      plank_seconds: optionalNumber().min(0),
      sit_to_stand_30s: optionalNumber().min(0),
      sit_and_reach_cm: optionalNumber(),
      balance_left_seconds: optionalNumber().min(0),
      balance_right_seconds: optionalNumber().min(0),
    }),
  }),
  yup.object({
    summary: yup.object({
      strengths: yup.string(),
      areas_for_improvement: yup.string(),
      corrective_priorities: yup.array().of(yup.string()),
      trainer_notes: yup.string(),
    }),
  }),
];
